/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// redux utils
import {createSlice, current} from '@reduxjs/toolkit';
// thunks
import {
    fetchPublisherPageviewForStackedCharts,
    fetchPublisherRevenueForOverview,
    fetchPublisherRevenueForStackedCharts
} from '../thunks/revenue_overview';
import {AVG_RPM, NET_REVENUE, PAGE_VIEWS} from "../../pages/dashboard/monetization/consts";

/*
OVERVIEW
DISPLAY
 - COMMENT_EMBED
 - QUESTION_EMBED
 - STANDALONE
 - ANCHOR
 - INTERSTITIAL
VIDEO
 - IN_STREAM_VIDEO
 - OUT_STREAM_VIDEO
 - VIDEO
APP
 - APP
TOTAL


Full Ad Management -> AD_UNIT
-STANDALONE, INTERSTITIAL, ANCHOR
Engagement products -> CEU, COMMENTING
Video -> VIDEO

 */

const MODEL = {
    lastUpdated: null,
    overview: {
        TOTAL: {
            revenue: 0,
            pageview: 0,
            rpm: 0,
            revenueChangeRate: 0,
            pageviewChangeRate: 0,
            rpmChangeRate: 0
        },
        DISPLAY: {
            revenue: 0,
            pageview: 0,
            rpm: 0,
            revenueChangeRate: 0,
            pageviewChangeRate: 0,
            rpmChangeRate: 0
        },
        VIDEO: {
            revenue: 0,
            pageview: 0,
            rpm: 0,
            revenueChangeRate: 0,
            pageviewChangeRate: 0,
            rpmChangeRate: 0
        }
    },
    details: {
        TOTAL: {
            Daily: {
                TOTAL: {}
            },
            Monthly: {
                TOTAL: {}
            },
            Weekly: {
                TOTAL: {}
            },
            Quarterly: {
                TOTAL: {}
            }
        },
        DISPLAY: {
            Daily: {
                TOTAL: {}
            },
            Monthly: {
                TOTAL: {}
            },
            Weekly: {
                TOTAL: {}
            },
            Quarterly: {
                TOTAL: {}
            }
        },
        VIDEO: {
            Daily: {
                TOTAL: {}
            },
            Monthly: {
                TOTAL: {}
            },
            Weekly: {
                TOTAL: {}
            },
            Quarterly: {
                TOTAL: {}
            }
        }
    },
    compareTo: {
        TOTAL: {
            Daily: {
                TOTAL: {}
            },
            Monthly: {
                TOTAL: {}
            },
            Weekly: {
                TOTAL: {}
            },
            Quarterly: {
                TOTAL: {}
            }
        },
        DISPLAY: {
            Daily: {
                TOTAL: {}
            },
            Monthly: {
                TOTAL: {}
            },
            Weekly: {
                TOTAL: {}
            },
            Quarterly: {
                TOTAL: {}
            }
        },
        VIDEO: {
            Daily: {
                TOTAL: {}
            },
            Monthly: {
                TOTAL: {}
            },
            Weekly: {
                TOTAL: {}
            },
            Quarterly: {
                TOTAL: {}
            }
        }
    },
    status: 'initial',
    isRevenueDataReceived: false,
    isRevenueDataForStackedChartReceived: false,
    isPageviewDataForStackedChartReceived: false,
}

/* ========== ~~~~~~~~~~ REVENUE SLICE : REDUCER ~~~~~~~~~~ ========== */
const initialState = {
    overviewChart: {...MODEL},
    stackedCharts: {...MODEL},
    productFilter: [], // revenue driver dropdown
}

export const revenueOverviewSlice = createSlice({
    name: 'revenueOverview',
    initialState,
    reducers: {
        setProductFilter: (state, action) => {
            state.productFilter = action.payload
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchPublisherRevenueForOverview.pending, (state, action) => {
                state.overviewChart.status = 'pending'
                state.overviewChart = {...MODEL}
            })
            .addCase(fetchPublisherRevenueForOverview.fulfilled, (state, action) => {
                const { latestUpdatedAt } = action.payload;
                const netRevenue = action.payload[NET_REVENUE];

                if (latestUpdatedAt) state.overviewChart.lastUpdated = latestUpdatedAt

                if (netRevenue.TOTAL.TOTAL.data > 0) {
                    Object.keys(state.overviewChart.details).forEach(product => {
                        Object.keys(state.overviewChart.details[product]).forEach(period => {
                            state.overviewChart.details[product][period].TOTAL = netRevenue[period.toUpperCase()][product];
                            if (netRevenue[period.toUpperCase()][product]) {
                                Object.keys(state.overviewChart.details[product][period].TOTAL).forEach(date => {
                                    const revenue = state.overviewChart.details[product][period].TOTAL[date].data;
                                    const revenueChangeRate = state.overviewChart.details[product][period].TOTAL[date].changeRate;
                                    state.overviewChart.details[product][period].TOTAL[date].revenue = revenue;
                                    state.overviewChart.details[product][period].TOTAL[date].revenueChangeRate = revenueChangeRate;
                                })
                            }
                        })
                    })

                    Object.keys(netRevenue.TOTAL).forEach(product => {
                        const revenue = netRevenue.TOTAL[product].data;
                        const revenueChangeRate = netRevenue.TOTAL[product].changeRate;

                        state.overviewChart.overview[product].revenue = revenue;
                        state.overviewChart.overview[product].revenueChangeRate = revenueChangeRate;
                    })
                }

                state.overviewChart.status = 'fulfilled'
                state.overviewChart.isRevenueDataReceived = true
            })
            .addCase(fetchPublisherRevenueForOverview.rejected, (state, action) => {
                state.overviewChart.status = 'rejected'
            })
            .addCase(fetchPublisherRevenueForStackedCharts.pending, (state, action) => {
                state.stackedCharts = {...MODEL}
                state.stackedCharts.status = 'pending'
            })
            .addCase(fetchPublisherRevenueForStackedCharts.fulfilled, (state, action) => {
                const { latestUpdatedAt } = action.payload;
                const netRevenue = action.payload[NET_REVENUE];

                if (latestUpdatedAt) state.stackedCharts.lastUpdated = latestUpdatedAt

                if (netRevenue.TOTAL.TOTAL.data > 0) {
                    // Set empty shell
                    Object.keys(state.stackedCharts.details.TOTAL).forEach(period => {
                        Object.keys(netRevenue[period.toUpperCase()]['TOTAL']).forEach(date => {
                            if (!state.stackedCharts.details.TOTAL[period]['TOTAL'][date]) state.stackedCharts.details.TOTAL[period]['TOTAL'][date] = {};
                        })
                    })
                    // populate detail data
                    Object.keys(state.stackedCharts.details.TOTAL).forEach(period => { // TOTAL is the only one matters here
                        Object.keys(netRevenue[period.toUpperCase()]['TOTAL']).forEach(date => {
                            const revenue = netRevenue[period.toUpperCase()]['TOTAL'][date].data;
                            const revenueChangeRate = netRevenue[period.toUpperCase()]['TOTAL'][date].changeRate;
                            state.stackedCharts.details.TOTAL[period]['TOTAL'][date].revenue = revenue;
                            state.stackedCharts.details.TOTAL[period]['TOTAL'][date].revenueChangeRate = revenueChangeRate;
                        })
                    })

                    // populate overview data, again, all we care about is TOTAL
                    const revenue = netRevenue.TOTAL.TOTAL.data;
                    const revenueChangeRate = netRevenue.TOTAL.TOTAL.changeRate;

                    state.stackedCharts.overview.TOTAL.revenue = revenue;
                    state.stackedCharts.overview.TOTAL.revenueChangeRate = revenueChangeRate;
                }

                state.stackedCharts.isRevenueDataForStackedChartReceived = true
                const { isPageviewDataForStackedChartReceived, isRevenueDataForStackedChartReceived } = state.stackedCharts
                if (isPageviewDataForStackedChartReceived && isRevenueDataForStackedChartReceived) state.stackedCharts.status = 'fulfilled'
            })
            .addCase(fetchPublisherRevenueForStackedCharts.rejected, (state, action) => {
                state.stackedCharts.status = 'rejected'
            })
            .addCase(fetchPublisherPageviewForStackedCharts.pending, (state, action) => {
                state.stackedCharts.status = 'pending'
            })
            .addCase(fetchPublisherPageviewForStackedCharts.fulfilled, (state, action) => {
                const { latestUpdatedAt } = action.payload;
                const pageViews = action.payload[PAGE_VIEWS];
                const avgRpm = action.payload[AVG_RPM];

                if (latestUpdatedAt) state.stackedCharts.lastUpdated = latestUpdatedAt

                if (pageViews.TOTAL.TOTAL.data > 0) {
                    // Set empty shell
                    Object.keys(state.stackedCharts.details.TOTAL).forEach(period => {
                        Object.keys(pageViews[period.toUpperCase()]['TOTAL']).forEach(date => {
                            if (!state.stackedCharts.details.TOTAL[period]['TOTAL'][date]) state.stackedCharts.details.TOTAL[period]['TOTAL'][date] = {};
                        })
                    })
                    // populate detail data
                    Object.keys(state.stackedCharts.details.TOTAL).forEach(period => { // TOTAL is the only one matters here
                        // There's no actual product breakdown for this
                        Object.keys(pageViews[period.toUpperCase()]['TOTAL']).forEach(date => {
                                const pageview = pageViews[period.toUpperCase()]['TOTAL'][date].data;
                                const pageviewChangeRate = pageViews[period.toUpperCase()]['TOTAL'][date].changeRate;
                                state.stackedCharts.details.TOTAL[period]['TOTAL'][date].pageview = pageview;
                                state.stackedCharts.details.TOTAL[period]['TOTAL'][date].pageviewChangeRate = pageviewChangeRate;
                            })

                        Object.keys(avgRpm[period.toUpperCase()]['TOTAL']).forEach(date => {
                                const rpm = avgRpm[period.toUpperCase()]['TOTAL'][date].data;
                                const rpmChangeRate = avgRpm[period.toUpperCase()]['TOTAL'][date].changeRate;
                                state.stackedCharts.details.TOTAL[period]['TOTAL'][date].rpm = rpm;
                                state.stackedCharts.details.TOTAL[period]['TOTAL'][date].rpmChangeRate = rpmChangeRate;
                            })
                    })

                    // populate overview data, again, all we care about is TOTAL
                    const pageview = pageViews.TOTAL.TOTAL.data;
                    const pageviewChangeRate = pageViews.TOTAL.TOTAL.changeRate;
                    const rpm = avgRpm.TOTAL.TOTAL.data;
                    const rpmChangeRate = avgRpm.TOTAL.TOTAL.changeRate;

                    state.stackedCharts.overview.TOTAL.pageview = pageview;
                    state.stackedCharts.overview.TOTAL.pageviewChangeRate = pageviewChangeRate;
                    state.stackedCharts.overview.TOTAL.rpm = rpm;
                    state.stackedCharts.overview.TOTAL.rpmChangeRate = rpmChangeRate;
                }

                state.stackedCharts.isPageviewDataForStackedChartReceived = true
                const { isPageviewDataForStackedChartReceived, isRevenueDataForStackedChartReceived } = state.stackedCharts
                if (isPageviewDataForStackedChartReceived && isRevenueDataForStackedChartReceived) state.stackedCharts.status = 'fulfilled'
            })
            .addCase(fetchPublisherPageviewForStackedCharts.rejected, (state, action) => {
                state.stackedCharts.status = 'rejected'
            })
    }
});

export const { setProductFilter } = revenueOverviewSlice.actions;
export default revenueOverviewSlice.reducer;
